import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronRight } from 'lucide-react';

const navigation = [
  { name: 'About', href: '/about' },
  { name: 'Practice Areas', href: '/practice-areas' },
  { name: 'Industries', href: '/industries' },
  { name: 'How We Work', href: '/how-we-work' },
  { name: 'Why Us', href: '/why-us' },
  { name: 'Contact', href: '/contact' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-testid="header">
      {/* Top bar */}
      <div className="bg-navy text-white text-sm">
        <div className="container-law py-2 flex justify-end items-center gap-6">
          <a href="mailto:info@kattorney.com" className="flex items-center gap-2 hover:text-green transition-colors">
            <Mail className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">info@kattorney.com</span>
          </a>
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden md:inline">Huntingdon Valley, PA</span>
          </span>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container-law">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="font-logo text-2xl md:text-3xl font-semibold tracking-tight">
              <span className="text-green">Erlichman</span>
              <span className="text-navy">Law</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${location.pathname === item.href ? 'active' : ''}`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/contact"
              className="btn-green text-sm"
              data-testid="nav-cta"
            >
              Contact Us
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" strokeWidth={1.5} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-6 border-t border-gray-100" data-testid="mobile-menu">
            <div className="flex flex-col pt-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`py-3 px-2 border-b border-gray-100 flex items-center justify-between ${
                    location.pathname === item.href
                      ? 'text-navy font-medium'
                      : 'text-gray-600'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                  <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              ))}
              <Link
                to="/contact"
                className="btn-green mt-4 text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="footer" data-testid="footer">
      <div className="container-law py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6">
              <span className="font-logo text-2xl font-semibold tracking-tight">
                <span className="text-green">Erlichman</span>
                <span className="text-white">Law</span>
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed max-w-md mb-6">
              Commercial and technology counsel for life sciences, healthcare technology, and data-driven businesses. We represent technology vendors selling into regulated enterprises.
            </p>
            <div className="divider mb-6"></div>
            <p className="text-white/50 text-xs">
              © {new Date().getFullYear()} Erlichman Law. All rights reserved.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-wider mb-6">Navigation</h4>
            <ul className="space-y-3">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-sm hover:text-green transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-wider mb-6">Contact</h4>
            <div className="space-y-4">
              <a
                href="mailto:info@kattorney.com"
                className="flex items-start gap-3 text-sm hover:text-green transition-colors"
              >
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                info@kattorney.com
              </a>
              <div className="flex items-start gap-3 text-sm text-white/70">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div>
                  2337 Philmont Avenue<br />
                  Suite 205<br />
                  Huntingdon Valley, PA 19006
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-law py-4 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          <p>Serving clients nationally from Pennsylvania</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};
